import React from "react";
import { useCrumbs } from "../../component/breadcrumbs";
import { Helmet } from "react-helmet-async";
import useLocale from "../../util/i18n";
import { InventoryData, deleteInventory, getInventory, getInventoryItems } from "../../api/inventory";
import { MDBBadge, MDBBtn, MDBCard, MDBCardHeader, MDBInput, MDBModal, MDBModalBody, MDBModalContent, MDBModalDialog, MDBModalFooter } from "mdb-react-ui-kit";
import { useSearchParams } from "react-router-dom";
import Pager from "../../component/pager";
import Toast from "../../component/toast";
import ExcelJS from "exceljs";
import Lightbox, { type LightBoxRef, type Urls } from "../../component/lightbox";
import { faCalendarXmark } from "@fortawesome/free-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAngleDown, faFileArrowDown, faImages, faSpinner, faTrashAlt } from "@fortawesome/free-solid-svg-icons";
import { useRbac } from "../../util/token";
import env from "../../env.json";
import TagInfo from "../../util/tag";
import type { SkuQty } from "../../api/erp";

export default function Shipment() {
    const {0: inventories, 1: setInventories} = React.useState<Array<InventoryData>>([]);
    const {0: total, 1: setTotal} = React.useState(0);
    const {0: search, 1: setSearch} = useSearchParams();
    const {0: loading, 1: setLoading} = React.useState(false);
    const {0: __} = useLocale();
    const {0: setCrumbs} = useCrumbs();
    const lightbox = React.useRef<LightBoxRef>(null);
    const showImgs = React.useCallback((urls: Urls) => {
        lightbox.current?.show(urls, 0);
    }, []);
    React.useEffect(() => {
        setLoading(true);
        search.delete('version');
        getInventory(search).then(response => {
            if (typeof response !== 'string') {
                setInventories(response.data);
                setTotal(response.total);
            }
            setLoading(false);
        });
    }, [search, setLoading]);
    React.useEffect(() => {
        setCrumbs(['Shipment Inventories']);
    }, []);
    return (
        <>
            <Helmet prioritizeSeoTags>
                <title>{__('Shipment Inventories')}</title>
            </Helmet>
            <div className="inventories" data-searchable>
                {inventories.length ? inventories.map(inventory => (
                    <Inventory key={inventory.id.toString()} inventory={inventory} lightbox={showImgs} />
                )) : (
                    <div className="empty">
                        {loading ? (
                            <FontAwesomeIcon icon={faSpinner} spin size="3x" />
                        ) : (
                            <>
                                <FontAwesomeIcon icon={faCalendarXmark} />
                                <span>{__('No Data to Display')}</span>
                            </>
                        )}
                    </div>
                )}
            </div>
            {inventories.length ? (<Pager total={total} search={search} onPage={setSearch} />) : null}
            <Search />
            <Lightbox ref={lightbox} />
        </>
    );
};

function Inventory(props: { inventory: InventoryData, lightbox: (urls: Urls) => void }) {
    const {0: open, 1: setOpen} = React.useState(false);
    const {0: items, 1: setItems} = React.useState<Array<SkuQty>>();
    const {0: __, 1: lang} = useLocale();
    let { 1: setSearch } = useSearchParams();
    const {0: isGranted} = useRbac();
    let images = React.useMemo(() => {
        let result = [];
        if (props.inventory.info.image) {
            for (let src of (typeof props.inventory.info.image === 'string' ? [props.inventory.info.image] : props.inventory.info.image)) {
                if (src.startsWith('data:') || src.includes('://')) {
                    result.push(src);
                } else {
                    result.push(env.RES_URL + src);
                }
            }
        }
        return result;
    }, [props.inventory.info.image]);
    let doExport = React.useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        e.preventDefault();
        let file = new ExcelJS.Workbook();
        const sheet = file.addWorksheet();
        sheet.getRow(1).getCell(1).value = 'Order ID';
        sheet.getRow(1).getCell(2).value = 'Shipment ID';
        sheet.getRow(1).getCell(3).value = 'Customer ID';
        sheet.getRow(1).getCell(4).value = 'Customer Name';
        sheet.getRow(2).getCell(1).value = props.inventory.info.order_id;
        sheet.getRow(2).getCell(2).value = props.inventory.info.shipment_id;
        sheet.getRow(2).getCell(3).value = props.inventory.info.customer_id;
        sheet.getRow(2).getCell(4).value = props.inventory.info.customer_name;
        sheet.getRow(3).getCell(1).value = 'SKU';
        sheet.getRow(3).getCell(2).value = 'Qty';
        let r = 4;
        for (let item of (items ?? [])) {
            let row = sheet.getRow(r++);
            row.getCell(1).value = item.sku;
            row.getCell(2).value = item.qty;
        }
        file.xlsx.writeBuffer().then(buffer => {
            let a = document.createElement('a');
            a.download = 'export#' + props.inventory.ref_id + '.xlsx';
            a.href = URL.createObjectURL(new Blob([buffer]));
            document.body.append(a);
            a.click();
            URL.revokeObjectURL(a.href);
            a.remove();
        });
    }, [items, props.inventory]);
    let doDelete = React.useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        e.preventDefault();
        if (window.confirm(__('Are you sure to delete this? The deleted data cannot be recovered.'))) {
            deleteInventory(props.inventory.id).then(response => {
                setSearch(search => {
                    let cloned = new URLSearchParams(search);
                    cloned.set('version', (parseInt(cloned.get('version') || '0') + 1).toString());
                    return cloned;
                });
                Toast.show(response);
            }, error => Toast.show(error, 'danger'));
        }
    }, [props.inventory, __]);
    const ref = React.useRef<HTMLUListElement>(null);
    const support = React.useRef((() => {
        let div = document.createElement('div');
        document.body.append(div);
        div.style.height = 'calc-size(auto,size)';
        let result = Boolean(div.style.height);
        div.remove();
        return result;
    })());
    React.useEffect(() => {
        if (open && typeof items === 'undefined') {
            getInventoryItems({ id: props.inventory.id }).then(response => {
                if (typeof response !== 'string') {
                    let ps: Array<Promise<void>> = [], data: Record<string, SkuQty> = {};
                    for (let item of (response ?? [])) {
                        ps.push(new Promise(resolve => {
                            TagInfo.fromEpc(item.sku, tag => {
                                let sku = tag.sku;
                                data[sku] ??= { sku, qty: 0, codes: [] };
                                data[sku].qty++;
                                data[sku].codes?.push(tag.code);
                                resolve();
                            });
                        }));
                    }
                    Promise.all(ps).then(() => setItems(Object.values(data)));
                }
                if (!support.current) {
                    window.setTimeout(() => ref.current && (ref.current.style.height = 'auto'), 100);
                }
            });
        }
    }, [open, items, setItems, props.inventory]);
    return (
        <MDBCard className="inventory">
            <MDBCardHeader>
                <div className="info">
                    {props.inventory.info ? (
                        <>
                            <span><span className="label">{__('Order ID')}</span><span className="value">{props.inventory.info.order_id || ''}</span></span>
                            <span><span className="label">{__('Shipment ID')}</span><span className="value">{props.inventory.info.shipment_id || ''}</span></span>
                            <span><span className="label">{__('Customer Name')}</span><span className="value">{props.inventory.info.customer_name || ''}</span></span>
                            <span><span className="label">{__('Customer ID')}</span><span className="value">{props.inventory.info.customer_id || ''}</span></span>
                        </>
                    ) : null}
                </div>
                <div className="text-nowrap">
                    <span className="time">
                        {Intl.DateTimeFormat(lang, { dateStyle: 'medium', timeStyle: 'short', hour12: false }).format(props.inventory.created_at * 1000)}
                    </span>
                    {isGranted('getInventoryItems') ? (
                        <MDBBtn className="toggler" type="button" color="link" onClick={setOpen.bind(null, !open)}>
                            <FontAwesomeIcon icon={faAngleDown} style={{ transform: 'rotate(' + (open ? '180deg' : 0) + ')' }} />
                        </MDBBtn>
                    ) : null}
                </div>
            </MDBCardHeader>
            <ul className="card-body" ref={ref} style={open ? { height: support.current ? 'calc-size(auto,size)' : ((ref.current?.scrollHeight || 0) + 20) + 'px', paddingTop: '10px', paddingBottom: '10px' } : { height: '0px' }}>
                {typeof items === 'undefined' ? (
                    <li className="loading"><FontAwesomeIcon icon={faSpinner} spin /></li>
                ) : (items.length ? (
                    <>
                        {items.map(item => (
                            <li key={item.sku} title={item.codes?.join('\n')}>
                                <span style={{ display: 'inline-block', minWidth: '15ch' }}>{item.sku}</span>
                                <MDBBadge className="ms-3">{item.qty}</MDBBadge>
                            </li>
                        ))}
                        <li className="btns">
                            {images.length ? (
                                <button type="button" onClick={props.lightbox.bind(null, images)}><FontAwesomeIcon icon={faImages} /></button>
                            ) : null}
                            <button type="button" onClick={doExport}><FontAwesomeIcon icon={faFileArrowDown} /></button>
                            <button type="button" onClick={doDelete}><FontAwesomeIcon icon={faTrashAlt} /></button>
                        </li>
                    </>
                ) : (
                    <li className="empty">
                        <FontAwesomeIcon icon={faCalendarXmark} />
                        <span>{__('No Data to Display')}</span>
                    </li>
                ))}
            </ul>
        </MDBCard>
    );
}

function Search() {
    const {0: open, 1: setOpen} = React.useState(false);
    const {0: search, 1: setSearch} = useSearchParams();
    const {0: value, 1: setValue} = React.useState(search.get('q') || '');
    const {0: __} = useLocale();
    let onSubmit = React.useCallback((e: React.FormEvent<HTMLFormElement>) => {
        e.stopPropagation();
        e.preventDefault();
        let cloned = new URLSearchParams(search);
        let data = new FormData(e.target as HTMLFormElement);
        if ((data.get('q') as string | null)?.length) {
            cloned.set('q', data.get('q') as string);
        } else {
            cloned.delete('q');
        }
        cloned.delete('page');
        cloned.delete('limit');
        setSearch(cloned);
        setOpen(false);
    }, [search, setSearch, setOpen]);
    React.useEffect(() => {
        let l = () => setOpen(true);
        document.addEventListener('search', l);
        return () => document.removeEventListener('search', l);
    }, [setOpen]);
    return (
        <MDBModal open={open} onClose={setOpen.bind(null, false)}>
            <MDBModalDialog>
                <MDBModalContent tag="form" onSubmit={onSubmit}>
                    <MDBModalBody>
                        <MDBInput type="search" name="q" value={value} onChange={e => setValue(e.target.value)} label={__('Search')} />
                    </MDBModalBody>
                    <MDBModalFooter>
                        <MDBBtn type="button" color="secondary" onClick={setOpen.bind(null, false)}>{__('Cancel')}</MDBBtn>
                        <MDBBtn type="submit">{__('Search')}</MDBBtn>
                    </MDBModalFooter>
                </MDBModalContent>
            </MDBModalDialog>
        </MDBModal>
    )
}
